import { EventBus } from '@pp/toolkit';

import { Logger } from '../../logger/Logger';

export enum AssetLoaderServiceState {
  IDLE,
  FETCHING_BLOCKING,
  UNBLOCKED,
}

export type ResourcePriorityType = 'block' | number;

export interface Resource<Priority> {
  kind: 'font' | 'image';
  priority: Priority;
  url: string;
}

export type ResourceMap = Record<string, Resource<ResourcePriorityType>>;

export class AssetLoaderServiceBase extends EventBus<{
  ready: () => void;
}> {
  private readonly PRIORITY_CAP = 6;
  private readonly SET_NEXT_PRIORITY_AFTER = 1_000;
  private readonly SET_NONBLOCKING_PRIORITIES_AFTER = 10_000;

  private _state: AssetLoaderServiceState = AssetLoaderServiceState.IDLE;

  set state(state: AssetLoaderServiceState) {
    this._state = state;
    if (this._state === AssetLoaderServiceState.UNBLOCKED) {
      this.dispatchEvent('ready');
    }
  }

  get state(): AssetLoaderServiceState {
    return this._state;
  }

  private createPreloadLinkElement(resource: Resource<ResourcePriorityType>): HTMLLinkElement {
    const link = document.createElement('link');
    link.as = resource.kind;
    // Fonts need `crossorigin` to avoid being fetched twice, unlike images.
    if (resource.kind === 'font') {
      link.crossOrigin = '';
    }
    link.href = resource.url;
    link.rel = 'preload';
    return link;
  }

  /** recursively sets resource priority via DOM link rel=preload tag
   * does not process priorities higher than PRIORITY_CAP
   */
  private schedulePreload(resources: Record<number, Resource<number>[]>, priority: number): void {
    if (priority < this.PRIORITY_CAP) {
      const resource = resources[priority];
      if (resource) {
        resource.forEach((resource) => {
          const link = this.createPreloadLinkElement(resource);
          document.head.appendChild(link);
        });
      }

      setTimeout(() => {
        this.schedulePreload(resources, ++priority);
      }, this.SET_NEXT_PRIORITY_AFTER);
    }
  }

  private awaitBlockingResources(resources: Resource<'block'>[]): Promise<void[]> {
    const promises: Promise<void>[] = resources.map((resource) => {
      return new Promise((resolve, reject) => {
        const link = this.createPreloadLinkElement(resource);
        document.head.appendChild(link);

        link.onload = (): void => resolve();
        link.onerror = (): void => reject(resource.url);
      });
    });

    return Promise.all(promises);
  }

  private getBucketizedResourses(resourceMap: ResourceMap): {
    resourcesByPriority: Record<number, Resource<number>[]>;
    blockingResources: Resource<'block'>[];
  } {
    const resourcesByPriority: Record<number, Resource<number>[]> = {};
    const blockingResources: Resource<'block'>[] = [];

    for (const key in resourceMap) {
      const resource = resourceMap[key];
      if (!resource) continue;

      if (resource.priority === 'block') {
        blockingResources.push(resource as Resource<'block'>);
        continue;
      }

      const bucket = resourcesByPriority[resource.priority] || [];
      bucket.push(resource as Resource<number>);
      resourcesByPriority[resource.priority] = bucket;
    }

    return { resourcesByPriority, blockingResources };
  }

  public init(resources: ResourceMap): Promise<void> {
    const { blockingResources, resourcesByPriority } = this.getBucketizedResourses(resources);
    let promise: Promise<void> = Promise.resolve();

    if (!blockingResources.length) {
      this.state = AssetLoaderServiceState.UNBLOCKED;
    } else {
      this.state = AssetLoaderServiceState.FETCHING_BLOCKING;
      promise = this.awaitBlockingResources(blockingResources)
        .then(() => {})
        .catch((link) => {
          // TODO: if some blocking resource fails to load, we should retry to fetch it a few times
          Logger.logFailure('prefetchAsset', { additional: link });
        })
        .finally(() => {
          this.state = AssetLoaderServiceState.UNBLOCKED;
        });
    }

    setTimeout(() => {
      this.schedulePreload(resourcesByPriority, 1);
    }, this.SET_NONBLOCKING_PRIORITIES_AFTER);

    return promise;
  }
}

export const AssetLoaderService = new AssetLoaderServiceBase();
